/**
 * CommandPalette — keyboard-first search / recents / actions overlay.
 *
 * Opened globally (Ctrl+K / ⌘K / "/"). With an empty query it lists the
 * user's recently opened work orders (1–9 jump straight to one); typing
 * runs the same debounced multi-source search the header dropdown uses
 * (work orders + invoice batches), plus accounts, locations and settings
 * sections (useSettingsSearch — sections from the first character), each of
 * which opens the matching settings dialog. A "Create work order" action row
 * is pinned at the bottom when the caller allows creation.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { theme, Kbd } from '@turn-connect/ui';
import type { InvoiceBatch } from '@turn-connect/shared';
import { getWorkOrderStatusColor, getWorkOrderStatusLabel } from '@turn-connect/utils';
import { WorkOrderSummary, workOrdersService } from '../../services/workOrders';
import { invoicingService } from '../../services/admin/invoicingService';
import type { SearchSelection } from '../navigation/GlobalHeader';
import { useRecentWorkOrders, RecentWorkOrder } from '../../hooks/useRecentWorkOrders';
import { GLOBAL_SEARCH_MIN_CHARS } from '../../utils/globalSearch';
import { useSettingsSearch, type SettingsSearchHit } from '../../hooks/useSettingsSearch';
import { CUSTOMER_KIND_LABELS, type Customer } from '@turn-connect/shared';
import { customerSearchDetail, searchCustomersAt, useCustomerSearchOn } from '../../features/customers/customerSearch';

// Above the app's de-facto dialog ceiling — the themed modal layer is 9000,
// but ~a dozen legacy dialogs (GuardDeclineDialog, WorkOrderDetail's photo
// overlay, VehicleFlowModal…) hardcode 10000/10001 — so the palette is usable
// from inside any of them. Below toasts (theme.zIndex.toast = 10400).
export const QUICK_KEYS_OVERLAY_Z = 10100;

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selection: SearchSelection) => void;
  /** Provided only when the current user/location can create work orders. */
  onCreateWorkOrder?: () => void;
  locationId?: string | null;
  /** The account in scope, for account settings results. */
  accountId?: string | null;
  /** Which accounts, locations and settings sections may be offered. */
  permissions?: string[];
}

type PaletteRow =
  | { kind: 'header'; text: string }
  | { kind: 'recent'; index: number; recent: RecentWorkOrder }
  | { kind: 'work_order'; data: WorkOrderSummary }
  | { kind: 'invoice_batch'; data: InvoiceBatch }
  | { kind: 'customer'; data: Customer }
  | { kind: 'settings'; hit: SettingsSearchHit }
  | { kind: 'create' }
  | { kind: 'empty'; text: string };

const isSelectable = (row: PaletteRow | undefined): boolean => !!row && row.kind !== 'header' && row.kind !== 'empty';

const vehicleLabel = (wo: WorkOrderSummary): string => {
  const v = wo.vehicle_info || wo.vehicle;
  const parts = v ? [v.year, v.make, v.model].filter(Boolean) : [];
  return [parts.join(' '), wo.name || undefined].filter(Boolean).join(' · ');
};

const relativeTime = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'now';
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr`;
  const d = Math.floor(hr / 24);
  return d === 1 ? 'yesterday' : `${d} d`;
};

const NO_PERMISSIONS: string[] = [];

// locationId no longer scopes the work-order search — universal search always
// spans the caller's full accessible scope — but it is the location whose
// settings sections are offered.
const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onSelect,
  onCreateWorkOrder,
  locationId = null,
  accountId = null,
  permissions = NO_PERMISSIONS,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<PaletteRow & { kind: 'work_order' | 'invoice_batch' }>>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const recents = useRecentWorkOrders();
  const customerSearchOn = useCustomerSearchOn(accountId, permissions);
  // Over an open settings dialog nothing is offered (useSettingsSearch reads
  // the open-dialog registry): the app opens one settings dialog at a time.
  const { hits: settingsHits, loading: settingsLoading } = useSettingsSearch(query, {
    permissions,
    accountId,
    locationId,
    enabled: isOpen,
  });

  // Reset and focus on open
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setResults([]);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Capture-phase Escape so the palette closes no matter where focus sits,
  // and the keypress never reaches an underlying Modal's document-level
  // Escape listener (which would close the work order detail instead).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  // Debounced multi-source search — mirrors the header dropdown: fan out to
  // work orders (location-scoped when one is selected) and invoice batches,
  // interleave so neither source can hide the other.
  useEffect(() => {
    if (!isOpen) return;
    const term = query.trim();
    // Too short to be worth a search (see GLOBAL_SEARCH_MIN_CHARS).
    if (term.length < GLOBAL_SEARCH_MIN_CHARS) {
      setResults([]);
      setCustomers([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const perTypeCap = 5;
      // Universal search spans the caller's full accessible scope, not just
      // the selected location (the backend scopes by accessible accounts when
      // no location is passed).
      const woPromise: Promise<WorkOrderSummary[]> = workOrdersService
        .searchWorkOrders(undefined, term, perTypeCap, true, true)
        .catch(() => []);
      const batchPromise: Promise<InvoiceBatch[]> = invoicingService.searchInvoiceBatches(term, perTypeCap).catch(() => []);
      // Customers at this store (accounts using Customers).
      const customerPromise = customerSearchOn ? searchCustomersAt(locationId, term, 3) : Promise.resolve([]);
      const [workOrders, batches, found] = await Promise.all([woPromise, batchPromise, customerPromise]);
      if (cancelled) return;
      setCustomers(found);
      const woRows = (workOrders || []).map((data) => ({ kind: 'work_order' as const, data }));
      const batchRows = (batches || []).map((data) => ({ kind: 'invoice_batch' as const, data }));
      const merged: Array<PaletteRow & { kind: 'work_order' | 'invoice_batch' }> = [];
      const max = Math.max(woRows.length, batchRows.length);
      for (let i = 0; i < max; i++) {
        if (i < woRows.length) merged.push(woRows[i]);
        if (i < batchRows.length) merged.push(batchRows[i]);
      }
      setResults(merged.slice(0, 8));
      setLoading(false);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, isOpen, locationId, customerSearchOn]);

  const rows = useMemo<PaletteRow[]>(() => {
    const term = query.trim();
    const out: PaletteRow[] = [];
    if (!term) {
      if (recents.length > 0) {
        out.push({ kind: 'header', text: 'Recent' });
        recents.forEach((recent, i) => out.push({ kind: 'recent', index: i + 1, recent }));
      } else {
        out.push({ kind: 'empty', text: 'Work orders you open will show up here for quick access.' });
      }
    } else if (results.length > 0 || customers.length > 0 || settingsHits.length > 0) {
      if (customers.length > 0) {
        out.push({ kind: 'header', text: 'Customers' });
        out.push(...customers.map((data) => ({ kind: 'customer' as const, data })));
      }
      const woRows = results.filter((r) => r.kind === 'work_order');
      const batchRows = results.filter((r) => r.kind === 'invoice_batch');
      if (woRows.length > 0) {
        out.push({ kind: 'header', text: 'Work orders' });
        out.push(...woRows);
      }
      if (batchRows.length > 0) {
        out.push({ kind: 'header', text: 'Invoices' });
        out.push(...batchRows);
      }
      const groups: Array<[SettingsSearchHit['kind'], string]> = [
        ['account', 'Accounts'],
        ['location', 'Locations'],
        ['section', 'Settings'],
      ];
      for (const [kind, text] of groups) {
        const hits = settingsHits.filter((h) => h.kind === kind);
        if (hits.length === 0) continue;
        out.push({ kind: 'header', text });
        out.push(...hits.map((hit) => ({ kind: 'settings' as const, hit })));
      }
    } else if (!loading && !settingsLoading) {
      out.push({ kind: 'empty', text: `No matches for “${term}”` });
    }
    if (onCreateWorkOrder) {
      out.push({ kind: 'header', text: 'Actions' });
      out.push({ kind: 'create' });
    }
    return out;
  }, [query, recents, results, customers, loading, settingsHits, settingsLoading, onCreateWorkOrder]);

  // Keep the selection on the first selectable row whenever the rows change
  useEffect(() => {
    const first = rows.findIndex((r) => isSelectable(r));
    setSelectedIndex(first);
  }, [rows]);

  const moveSelection = (dir: 1 | -1) => {
    let i = selectedIndex;
    do {
      i += dir;
    } while (i >= 0 && i < rows.length && !isSelectable(rows[i]));
    if (i >= 0 && i < rows.length) {
      setSelectedIndex(i);
      const el = listRef.current?.children[i] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  };

  const choose = (row: PaletteRow | undefined) => {
    if (!isSelectable(row) || !row) return;
    onClose();
    if (row.kind === 'recent') {
      onSelect({ type: 'work_order', id: row.recent.id });
    } else if (row.kind === 'work_order') {
      onSelect({ type: 'work_order', id: row.data.id });
    } else if (row.kind === 'invoice_batch') {
      onSelect({ type: 'invoice_batch', id: row.data.id, displayId: row.data.display_id || '' });
    } else if (row.kind === 'customer') {
      onSelect({ type: 'customer', id: row.data.id, locationId: locationId ?? '' });
    } else if (row.kind === 'settings') {
      onSelect({ type: 'settings', target: row.hit.target });
    } else if (row.kind === 'create') {
      onCreateWorkOrder?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape is handled by the capture-phase document listener above so it
    // works regardless of focus.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveSelection(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveSelection(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Fall back to the top match when the selection index hasn't caught up
      // with freshly arrived results (it's synced in an effect) — a fast
      // type-then-Enter must open the first result, like the header search.
      const row = isSelectable(rows[selectedIndex]) ? rows[selectedIndex] : rows.find(isSelectable);
      choose(row);
    } else if (e.key.toLowerCase() === 'n' && e.ctrlKey && !e.metaKey) {
      // The bare 'n' quick key can't work here (focus is in the search
      // input, so 'n' must type). Ctrl+N is the in-palette equivalent —
      // deliberately Ctrl on every platform: Cmd+N is the browser's
      // reserved new-window shortcut on macOS and never reaches the page,
      // while plain Ctrl+N is free there.
      e.preventDefault();
      const createRow = rows.find((r) => r.kind === 'create');
      if (createRow) choose(createRow);
    } else if (/^[1-9]$/.test(e.key) && !query.trim()) {
      // Digit jump applies only to the empty-query recents list, so typed
      // numeric searches still work once any character is in the box.
      const target = rows.find((r) => r.kind === 'recent' && r.index === parseInt(e.key, 10));
      if (target) {
        e.preventDefault();
        choose(target);
      }
    }
  };

  if (!isOpen) return null;

  const renderRow = (row: PaletteRow, i: number) => {
    if (row.kind === 'header') {
      return (
        <li
          key={`h-${row.text}`}
          style={{
            fontSize: '0.65625rem',
            fontWeight: 600,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: theme.colors.text.tertiary,
            padding: '0.5rem 0.625rem 0.25rem',
          }}
        >
          {row.text}
        </li>
      );
    }
    if (row.kind === 'empty') {
      return (
        <li key="empty" style={{ color: theme.colors.text.tertiary, fontSize: '0.75rem', padding: '0.875rem 0.625rem' }}>
          {row.text}
        </li>
      );
    }

    const selected = i === selectedIndex;
    const rowStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.5rem 0.625rem',
      borderRadius: '0.4375rem',
      border: `1px solid ${selected ? 'rgba(218, 86, 44, 0.55)' : 'transparent'}`,
      backgroundColor: selected ? 'rgba(218, 86, 44, 0.12)' : 'transparent',
      cursor: 'pointer',
    };
    const titleStyle: React.CSSProperties = { fontSize: '0.75rem', fontWeight: 600, color: theme.colors.text.primary };
    const subStyle: React.CSSProperties = {
      color: theme.colors.text.secondary,
      fontSize: '0.71875rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
    // Current department — the "where is it right now" a searcher usually wants.
    const deptChipStyle: React.CSSProperties = {
      fontSize: '0.65625rem',
      fontWeight: 600,
      color: theme.colors.text.secondary,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: '999px',
      padding: '0.125rem 0.5rem',
      whiteSpace: 'nowrap',
      maxWidth: '9rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    if (row.kind === 'recent') {
      const r = row.recent;
      return (
        <li key={`r-${r.id}`} style={rowStyle} onClick={() => choose(row)} onMouseEnter={() => setSelectedIndex(i)}>
          {/* Digit-jump covers 1–9 only; rows past the 9th get a muted dot. */}
          <Kbd style={{ minWidth: '1em', textAlign: 'center', color: selected ? theme.colors.brand.primary : undefined }}>
            {row.index <= 9 ? row.index : '·'}
          </Kbd>
          <span style={{ minWidth: 0, flex: 1 }}>
            <div style={titleStyle}>{r.displayId || r.id}</div>
            {r.label ? <div style={subStyle}>{r.label}</div> : null}
          </span>
          {r.departmentName ? <span style={deptChipStyle}>{r.departmentName}</span> : null}
          <span style={{ color: theme.colors.text.tertiary, fontSize: '0.6875rem', minWidth: '3.25rem', textAlign: 'right' }}>
            {relativeTime(r.openedAt)}
          </span>
        </li>
      );
    }
    if (row.kind === 'work_order') {
      const wo = row.data;
      const statusColor = getWorkOrderStatusColor(wo.status);
      return (
        <li key={`w-${wo.id}`} style={rowStyle} onClick={() => choose(row)} onMouseEnter={() => setSelectedIndex(i)}>
          <span style={{ minWidth: 0, flex: 1 }}>
            <div style={titleStyle}>{wo.work_order_display_id || wo.id}</div>
            {vehicleLabel(wo) ? <div style={subStyle}>{vehicleLabel(wo)}</div> : null}
          </span>
          {wo.department_name ? <span style={deptChipStyle}>{wo.department_name}</span> : null}
          <span
            style={{
              fontSize: '0.65625rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderRadius: '999px',
              padding: '0.15625rem 0.5rem',
              color: statusColor,
              backgroundColor: `${statusColor}26`,
              whiteSpace: 'nowrap',
            }}
          >
            {getWorkOrderStatusLabel(wo.status)}
          </span>
        </li>
      );
    }
    if (row.kind === 'invoice_batch') {
      const batch = row.data;
      return (
        <li key={`b-${batch.id}`} style={rowStyle} onClick={() => choose(row)} onMouseEnter={() => setSelectedIndex(i)}>
          <span style={{ minWidth: 0, flex: 1 }}>
            <div style={titleStyle}>{batch.display_id || batch.id}</div>
            <div style={subStyle}>Invoice</div>
          </span>
        </li>
      );
    }
    if (row.kind === 'customer') {
      const c = row.data;
      return (
        <li
          key={`c-${c.id}`}
          style={rowStyle}
          onClick={() => choose(row)}
          onMouseEnter={() => setSelectedIndex(i)}
          data-testid="palette-customer-result"
        >
          <span style={{ minWidth: 0, flex: 1 }}>
            <div style={titleStyle}>{c.name}</div>
            <div style={subStyle}>{customerSearchDetail(c, CUSTOMER_KIND_LABELS[c.kind] ?? c.kind)}</div>
          </span>
        </li>
      );
    }
    if (row.kind === 'settings') {
      const { hit } = row;
      return (
        <li
          key={hit.key}
          style={rowStyle}
          onClick={() => choose(row)}
          onMouseEnter={() => setSelectedIndex(i)}
          data-testid={`palette-${hit.kind}-result`}
        >
          <span style={{ minWidth: 0, flex: 1 }}>
            <div style={titleStyle}>{hit.label}</div>
            <div style={subStyle}>{hit.detail}</div>
          </span>
        </li>
      );
    }
    // create
    return (
      <li key="create" style={rowStyle} onClick={() => choose(row)} onMouseEnter={() => setSelectedIndex(i)}>
        <span style={{ ...titleStyle, color: theme.colors.brand.primary }}>+ Create work order</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
          <Kbd>ctrl</Kbd>
          <Kbd>n</Kbd>
        </span>
      </li>
    );
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: QUICK_KEYS_OVERLAY_Z, backgroundColor: 'rgba(0, 0, 0, 0.62)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="Search and quick actions"
        style={{
          position: 'absolute',
          left: '50%',
          top: '5rem',
          transform: 'translateX(-50%)',
          width: 'min(34rem, calc(100vw - 2rem))',
          backgroundColor: theme.colors.background.secondary,
          border: `1px solid ${theme.colors.border.light}`,
          borderRadius: '0.625rem',
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.55)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            padding: '0.75rem 1rem',
            borderBottom: `1px solid ${theme.colors.border.default}`,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search customers, work orders, invoices, settings… or pick a recent"
            style={{
              flex: 1,
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: theme.colors.text.primary,
              fontSize: '0.8125rem',
            }}
          />
          {loading && (
            <div
              style={{
                width: 14,
                height: 14,
                border: '2px solid var(--tc-border-light, #333333)',
                borderTop: `2px solid ${theme.colors.brand.primary}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          )}
        </div>

        <ul ref={listRef} style={{ listStyle: 'none', margin: 0, padding: '0.375rem', maxHeight: '19rem', overflowY: 'auto' }}>
          {rows.map(renderRow)}
        </ul>

        <div
          style={{
            display: 'flex',
            gap: '0.875rem',
            alignItems: 'center',
            padding: '0.5625rem 1rem',
            borderTop: `1px solid ${theme.colors.border.default}`,
            color: theme.colors.text.tertiary,
            fontSize: '0.6875rem',
          }}
        >
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
          </span>
          {!query.trim() && recents.length > 0 && (
            <span>
              <Kbd>1</Kbd>–<Kbd>9</Kbd> open recent
            </span>
          )}
          <span>
            <Kbd>Enter</Kbd> open
          </span>
          <span style={{ marginLeft: 'auto' }}>
            <Kbd>Esc</Kbd> close
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
